import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useNotificationProcessors } from "services/hooks/rentals";

const ProcessTriggersTab = () => {
  const {
    isRunningTenantProcess,
    isRunningPayoutProcess,
    processError,
    runTenantProcess,
    runPayoutProcess,
  } = useNotificationProcessors();
  const [runDate, setRunDate] = useState("");
  const [dryRun, setDryRun] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [result, setResult] = useState(null);

  const getPayload = () => ({
    run_date: runDate || undefined,
    dry_run: dryRun,
  });

  const runProcessor = async (type) => {
    setMessage({ type: "", text: "" });
    const response = type === "TENANT"
      ? await runTenantProcess(getPayload())
      : await runPayoutProcess(getPayload());
    if (response.success) {
      setResult(response.data);
      setMessage({
        type: "success",
        text: type === "TENANT" ? "Tenant notifications processed." : "Payout notifications processed.",
      });
    } else {
      setMessage({ type: "error", text: response.message || "Process trigger failed." });
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Process Triggers
      </Typography>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      {processError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {processError}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack spacing={2}>
          <TextField
            label="Run Date (optional)"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={runDate}
            onChange={(event) => setRunDate(event.target.value)}
            helperText="Leave blank to process for today."
          />
          <FormControlLabel
            control={<Switch checked={dryRun} onChange={(event) => setDryRun(event.target.checked)} />}
            label="Dry Run (no sends)"
          />
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Button
              variant="contained"
              onClick={() => runProcessor("TENANT")}
              disabled={isRunningTenantProcess || isRunningPayoutProcess}
            >
              {isRunningTenantProcess ? <CircularProgress size={24} /> : "Run Tenant Processor"}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => runProcessor("PAYOUT")}
              disabled={isRunningTenantProcess || isRunningPayoutProcess}
            >
              {isRunningPayoutProcess ? <CircularProgress size={24} /> : "Run Payout Processor"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {result && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Latest Run Result
          </Typography>
          <Typography variant="body2">Processed: {result.processed ?? 0}</Typography>
          <Typography variant="body2">Sent: {result.sent ?? 0}</Typography>
          <Typography variant="body2">Failed: {result.failed ?? 0}</Typography>
          <Typography variant="body2">Skipped: {result.skipped ?? 0}</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ProcessTriggersTab;
